import { fetchLawyerList, deleteLawyer, createLawyer } from './fetches';
import { fetchLawyersTable } from '../../services/lawyerFetches';
import { fetchExpertise } from '../../services/proposalsRoutes';
import { IconEdit, IconX, IconPlus, IconTable, IconChevronUp, IconChevronDown, IconFilter } from '@tabler/icons-react';
// import path from 'path';

import {
  Table,
  Anchor,
  Button,
  Modal,
  Center,
  Stack,
  ScrollArea,
  Input,
  Group,
  TextInput,
  UnstyledButton,
  Text,
  Loader,
  MultiSelect,
  Select,
  Badge,
  Paper,
  Collapse,
  ActionIcon,
} from '@mantine/core';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, Link } from 'react-router-dom';
import { LawyerHeader } from '../../interfaces/types';
import { useTranslation } from 'react-i18next';
import useLocalStorage from '@/hooks/useLocalStorage';

// Enhanced lawyer interface with more fields
interface EnhancedLawyer {
  LawyerName: string;
  last_edited?: Date;
  ProfessionalTitleEn?: string | null;
  ProfessionalTitlePl?: string | null;
  PositionEn?: string | null;
  PositionPl?: string | null;
  Expertise?: string[];
}

type SortField = 'LawyerName' | 'last_edited' | 'PositionEn' | 'PositionPl';
type SortDirection = 'asc' | 'desc';

type FilterField = 'LawyerName' | 'Position' | 'Title' | 'Expertise';

export default function EditLawyer() {
  const [lawyerList, setLawyerList] = useState<EnhancedLawyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useLocalStorage<string>('viewLawyers.searchTerm', '');
  const [sortField, setSortField] = useLocalStorage<SortField>('viewLawyers.sortField', 'last_edited');
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('viewLawyers.sortDirection', 'desc');
  const [filterField, setFilterField] = useLocalStorage<FilterField>('viewLawyers.filterField', 'LawyerName');
  const [selectedExpertise, setSelectedExpertise] = useLocalStorage<string[]>('viewLawyers.selectedExpertise', []);
  const [availableExpertise, setAvailableExpertise] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useLocalStorage<boolean>('viewLawyers.showFilters', false);
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLawyerName, setNewLawyerName] = useState('');

  const getLawyerList = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessTokenSilently();
      
      // Use the enhanced fetchLawyersTable instead of fetchLawyerList
      const fetchedData = await fetchLawyersTable(accessToken);
      
      const modifiedData = fetchedData?.map((item: EnhancedLawyer) => {
        return {
          ...item,
          last_edited: item.last_edited ? new Date(item.last_edited) : new Date("2024-01-01"),
        };
      });
      setLawyerList(modifiedData ? modifiedData : []);
    } catch (error) {
      console.error('Error fetching lawyer list:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  // Fetch available expertise options
  const getExpertiseOptions = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const expertise = await fetchExpertise(accessToken);
      setAvailableExpertise(expertise);
    } catch (error) {
      console.error('Error fetching expertise:', error);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    getLawyerList();
    getExpertiseOptions();
  }, [getLawyerList, getExpertiseOptions]);

  const handleCreateLawyer = async () => {
    const accessToken = await getAccessTokenSilently();
    const response = await createLawyer(accessToken, { LawyerName: newLawyerName });
    navigate(`/editlawyer/${newLawyerName}`);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'LawyerName' ? 'asc' : 'desc'); // Default to desc for dates, asc for names
    }
  };

  // Dynamic placeholder based on selected filter field
  const getSearchPlaceholder = () => {
    switch (filterField) {
      case 'LawyerName':
        return t('searchByName');
      case 'Position':
        return t('searchByPosition');
      case 'Title':
        return t('searchByTitle');
      case 'Expertise':
        return t('searchByExpertise');
      default:
        return t('searchByName');
    }
  };

  const sortedAndFilteredLawyerList = useMemo(() => {
    let filtered = lawyerList.filter((lawyer) => {
      // Text search based on selected filter field
      const searchLower = searchTerm.toLowerCase();
      let matchesText = false;

      switch (filterField) {
        case 'LawyerName':
          matchesText = lawyer.LawyerName.toLowerCase().includes(searchLower);
          break;
        case 'Position':
          matchesText = 
            (lawyer.PositionEn?.toLowerCase().includes(searchLower) ?? false) ||
            (lawyer.PositionPl?.toLowerCase().includes(searchLower) ?? false);
          break;
        case 'Title':
          matchesText = 
            (lawyer.ProfessionalTitleEn?.toLowerCase().includes(searchLower) ?? false) ||
            (lawyer.ProfessionalTitlePl?.toLowerCase().includes(searchLower) ?? false);
          break;
        case 'Expertise':
          matchesText = lawyer.Expertise?.some(exp => 
            exp.toLowerCase().includes(searchLower)
          ) ?? false;
          break;
        default:
          matchesText = lawyer.LawyerName.toLowerCase().includes(searchLower);
      }

      // Expertise filter
      const matchesExpertise = selectedExpertise.length === 0 || 
        selectedExpertise.every(exp => lawyer.Expertise?.includes(exp));

      return (searchTerm === '' || matchesText) && matchesExpertise;
    });

    return filtered.sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      if (sortField === 'LawyerName') {
        // Use Polish locale-aware string comparison
        const comparison = a.LawyerName.localeCompare(b.LawyerName, 'pl', { 
          sensitivity: 'base',
          numeric: true 
        });
        return sortDirection === 'asc' ? comparison : -comparison;
      } else if (sortField === 'last_edited') {
        aValue = a.last_edited || new Date(0);
        bValue = b.last_edited || new Date(0);
        
        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      } else if (sortField === 'PositionEn') {
        aValue = a.PositionEn || '';
        bValue = b.PositionEn || '';
        const comparison = aValue.toString().localeCompare(bValue.toString(), 'en');
        return sortDirection === 'asc' ? comparison : -comparison;
      } else if (sortField === 'PositionPl') {
        aValue = a.PositionPl || '';
        bValue = b.PositionPl || '';
        const comparison = aValue.toString().localeCompare(bValue.toString(), 'pl');
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return 0;
    });
  }, [lawyerList, searchTerm, filterField, selectedExpertise, sortField, sortDirection]);

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    const icon = isActive ? (
      sortDirection === 'asc' ? (
        <IconChevronUp size={14} />
      ) : (
        <IconChevronDown size={14} />
      )
    ) : null;

    return (
      <Table.Th>
        <UnstyledButton onClick={() => handleSort(field)}>
          <Group gap={4} justify="space-between">
            <Text fw={500}>{children}</Text>
            {icon}
          </Group>
        </UnstyledButton>
      </Table.Th>
    );
  };

  if (loading) {
    return (
      <Center style={{ height: '50vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="lg" w="100%">
      <Group justify="end" w="100%">
        <Button
          variant="light"
          color="black"
          leftSection={<IconTable size={16} />}
          onClick={() => navigate('/lawyertable')}
        >
          {t('viewLawyerExpertiseTable')}
        </Button>
      </Group>

      {/* Enhanced Search and Filter Section */}
      <Paper p="md" w="100%" withBorder>
        <Group justify="space-between" align="end" w="100%" mb="md">
          <Group gap="md" align="end">
            <Select
              label={t('searchIn')}
              data={[
                { value: 'LawyerName', label: t('nameShort') },
                { value: 'Position', label: t('positionShort') },
                { value: 'Title', label: t('titleShort') },
                { value: 'Expertise', label: t('expertiseShort') },
              ]}
              value={filterField}
              onChange={(value) => setFilterField(value as FilterField)}
              w={160}
            />
            <TextInput
              placeholder={getSearchPlaceholder()}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              w={300}
            />
            <ActionIcon
              variant="light"
              onClick={() => setShowFilters(!showFilters)}
              title={t('advancedFilters')}
              size="lg"
            >
              <IconFilter size={16} />
            </ActionIcon>
          </Group>
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={() => setIsModalOpen(true)}
          >
            {t('addLawyer')}
          </Button>
        </Group>

        <Collapse in={showFilters}>
          <MultiSelect
            label={t('filterByExpertise')}
            placeholder={t('selectExpertise')}
            data={availableExpertise}
            value={selectedExpertise}
            onChange={setSelectedExpertise}
            searchable
            clearable
          />
        </Collapse>
      </Paper>

      <Table.ScrollContainer minWidth={800} style={{ margin: '0 1rem' }}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <SortableHeader field="LawyerName">{t('lawyerName')}</SortableHeader>
              <SortableHeader field="PositionEn">{t('positionEn')}</SortableHeader>
              <SortableHeader field="PositionPl">{t('positionPl')}</SortableHeader>
              <Table.Th>{t('expertise')}</Table.Th>
              <SortableHeader field="last_edited">{t('dateEdited')}</SortableHeader>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedAndFilteredLawyerList.length > 0 ? (
              sortedAndFilteredLawyerList.map((row) => (
                <EnhancedLawyerRow key={row.LawyerName} row={row} onDelete={getLawyerList} />
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                  <Text c="dimmed">
                    {searchTerm || selectedExpertise.length > 0 ? t('noMatchFiles') : t('noLawyersFound')}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Modal opened={isModalOpen} onClose={() => setIsModalOpen(false)} title={t('addLawyer')}>
        <Center>
          <Group gap="md" w="50%" miw="300px">
            <TextInput
              label={t('name')}
              name="LawyerName"
              value={newLawyerName}
              onChange={(e) => {
                setNewLawyerName(e.target.value);
              }}
              required
            />
            <Group style={{ justifyContent: 'flex-end' }} mt="lg">
              <Button type="button" mt="md" onClick={handleCreateLawyer}>
                {t('save')}
              </Button>
            </Group>
          </Group>
        </Center>
      </Modal>
    </Stack>
  );
}

function EnhancedLawyerRow({ row, onDelete }: { row: EnhancedLawyer; onDelete: () => void }) {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const lastEditDate = row.last_edited ? new Date(row.last_edited) : new Date("2024-01-01");
  const formattedDate = lastEditDate.toISOString().split('T')[0];

  const handleEditClick = () => {
    navigate(`/editlawyer/${row.LawyerName}`);
  };

  const handleDeleteClick = () => {
    async function deleteProposalData() {
      const accessToken = await getAccessTokenSilently();
      await deleteLawyer(accessToken, row.LawyerName);
      onDelete(); // Call the callback function after deletion
    }
    deleteProposalData();
  };

  return (
    <Table.Tr key={row.LawyerName}>
      <Table.Td>
        <Anchor component={Link} to={`/editlawyer/${row.LawyerName}`} fz="sm">
          {row.LawyerName}
        </Anchor>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {row.PositionEn || '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {row.PositionPl || '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4}>
          {row.Expertise && row.Expertise.length > 0 ? (
            row.Expertise.slice(0, 3).map((exp, idx) => (
              <Badge key={idx} variant="light" size="xs">
                {exp}
              </Badge>
            ))
          ) : (
            <Text size="xs" c="dimmed">-</Text>
          )}
          {row.Expertise && row.Expertise.length > 3 && (
            <Badge variant="outline" size="xs">
              +{row.Expertise.length - 3}
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>{formattedDate}</Table.Td>
      <Table.Td style={{ textAlign: 'right' }}>
        <Button
          variant="light"
          color="green"
          mt="lg"
          style={{ width: 'fit-content', marginRight: '8px' }}
          leftSection={<IconEdit size={16} />}
          onClick={handleEditClick}
        >
          {t('edit')}
        </Button>

        <Button
          variant="light"
          color="red"
          mt="lg"
          style={{ width: 'fit-content' }}
          leftSection={<IconX size={16} />}
          onClick={() => setIsModalOpen(true)}
        >
          {t('delete')}
        </Button>
        <Modal
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={t('confirmDeletion')}
        >
          <div>{t('confirmDeleteProposal')}</div>
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '12px' }}
          >
            <Button color="gray" onClick={() => setIsModalOpen(false)}>
              {t('cancel')}
            </Button>
            <Button color="red" onClick={handleDeleteClick}>
              {t('confirm')}
            </Button>
          </div>
        </Modal>
      </Table.Td>
    </Table.Tr>
  );
}
